'use client';
import React, { useRef, useEffect, useState } from 'react';

const googleReviews = [
  {
    name: 'Deepika Verma',
    rating: 5,
    text: 'Shruti ji is an amazing healer. Her Reiki sessions have completely transformed my energy and health. Highly recommended!',
    date: '2 weeks ago',
    initial: 'D',
    color: '#9b7fe8',
  },
  {
    name: 'Amit Kumar',
    rating: 5,
    text: 'Best healing experience I have ever had. The Past Life Regression session gave me so much clarity about my current life challenges.',
    date: '1 month ago',
    initial: 'A',
    color: '#c9a84c',
  },
  {
    name: 'Rekha Singh',
    rating: 5,
    text: 'Shruti\'s Akashic Record Reading was incredibly accurate and insightful. She helped me understand my soul\'s journey beautifully.',
    date: '3 weeks ago',
    initial: 'R',
    color: '#7c5cbf',
  },
  {
    name: 'Suresh Patel',
    rating: 5,
    text: 'The Family Constellation session was deeply healing. I could feel the shift in my family dynamics immediately after the session.',
    date: '1 month ago',
    initial: 'S',
    color: '#b8a0e8',
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <span key={i} className={`text-sm ${i < rating ? 'text-[#c9a84c]' : 'text-[#c9a84c]/20'}`}>★</span>
      ))}
    </div>
  );
}

export default function GoogleReviewsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="google-reviews"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #130a24 0%, #1a0d30 50%, #130a24 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
              <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Google Reviews</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl font-bold mb-6"
              style={{
                background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              What Clients Say on Google
            </h2>

            {/* Google Rating Badge */}
            <div
              className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl mb-8"
              style={{
                background: 'rgba(22, 12, 40, 0.85)',
                border: '1px solid rgba(201,168,76,0.35)',
                boxShadow: '0 4px 30px rgba(201,168,76,0.12)',
              }}
            >
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <div>
                  <div className="text-[#e8dff5] text-xs font-medium">Google Rating</div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-[#c9a84c] text-base">★</span>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className="w-px h-10 self-center"
                style={{ background: 'rgba(201,168,76,0.3)' }}
              />
              <div className="text-center">
                <div
                  className="font-display text-3xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  5.0
                </div>
                <div className="text-[#c4b5fd]/55 text-xs">Average Rating</div>
              </div>
              <div
                className="w-px h-10 self-center"
                style={{ background: 'rgba(201,168,76,0.3)' }}
              />
              <div className="text-center">
                <div
                  className="font-display text-3xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  100+
                </div>
                <div className="text-[#c4b5fd]/55 text-xs">Reviews</div>
              </div>
            </div>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {googleReviews.map((review, index) => (
              <div
                key={review.name}
                className="rounded-2xl p-5"
                style={{
                  background: 'rgba(22, 12, 40, 0.80)',
                  border: '1px solid rgba(201,168,76,0.18)',
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.7s ease ${index * 100}ms, transform 0.7s ease ${index * 100}ms`,
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                    style={{ background: review.color }}
                  >
                    {review.initial}
                  </div>
                  <div>
                    <div className="text-[#e8dff5] font-medium text-sm">{review.name}</div>
                    <div className="text-[#c4b5fd]/40 text-xs">{review.date}</div>
                  </div>
                  <svg viewBox="0 0 24 24" className="w-5 h-5 ml-auto flex-shrink-0" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <StarRating rating={review.rating} />
                <p className="text-[#c4b5fd]/65 text-xs leading-relaxed mt-3 italic">
                  &ldquo;{review.text}&rdquo;
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://g.page/r/shreevidhahealinghub/review"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-gold inline-block"
            >
              View All Google Reviews
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
